import React, { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBuilding, FaEnvelope, FaMapMarkerAlt, FaPhoneAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { FaCakeCandles } from 'react-icons/fa6';
import { Label, ProfileDataType, ProfileField } from '../../../types/Profile';
import { profileDataTypeToString } from '../../../utils/stringUtils';
import { createEmptyField } from '../../../utils/formatUtils';
import ProfileFieldForm from '../forms/ProfileFieldForm';
import ModalLayout from '../layouts/ModalLayout';
import { IsValidField } from '../../../utils/validationUtils';
import userService from '../../../services/userService';
import CancelSaveButton from '../misc/CancelSaveButton';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_300,
  COLOR_PRIMARY_900,
  COLOR_SECONDARY_200,
  COLOR_SECONDARY_50,
} from '../../../constants';

interface ProfileModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  type: ProfileDataType;
  modalType: 'CREATE' | 'EDIT';
  field?: ProfileField;
}

export default function ProfileModal({
  open,
  setOpen,
  type,
  field,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  modalType,
}: ProfileModalProps): JSX.Element {
  const [modalField, setModalField] = useState<ProfileField>();
  const [validate, setValidate] = useState<boolean>(false);
  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (!open) return;
    setValidate(false);
    if (field) setModalField(field);
    else setModalField(createEmptyField(type));
  }, [open]);

  const getHeaderItems = (): JSX.Element => {
    switch (type) {
      case ProfileDataType.EMAIL:
        return <FaEnvelope className="icon h-6 w-6 text-xl text-secondary-50" aria-hidden="true" />;
      case ProfileDataType.ADDRESS:
        // eslint-disable-next-line react/jsx-no-undef
        return <FaMapMarkerAlt className="icon h-6 w-6 text-secondary-50" aria-hidden="true" />;
      case ProfileDataType.PHONENUMBER:
        return <FaPhoneAlt className="icon h-6 w-6 text-secondary-50" aria-hidden="true" />;
      case ProfileDataType.BIRTHDATE:
        return <FaCakeCandles className="icon h-6 w-6 text-secondary-50" aria-hidden="true" />;
      case ProfileDataType.BUSINESSNAME:
        return <FaBuilding className="icon h-6 w-6 text-secondary-50" aria-hidden="true" />;
      default:
        return <></>;
    }
  };

  const handleSubmit = async () => {
    if (!modalField?.label) {
      toast.error(t('toast.error.label'));
      return;
    }
    setValidate(true);
    if (!IsValidField(modalField, false)) return;

    await userService.setFields([modalField]);

    setOpen(false);
    setModalField(createEmptyField(type));
  };

  const labelOptions = [
    { label: 'General', value: Label.GENERAL },
    { label: 'Work', value: Label.WORK },
    { label: 'Private', value: Label.PRIVATE },
  ];

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      className="h-a z-50 mx-6 w-fit min-w-[450px] max-w-[600px] rounded-[20px] bg-secondary-50 px-10 py-6 pb-4 pt-5">
      <div className="flex w-full flex-col">
        <div className="mb-2 flex items-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary">
            {getHeaderItems()}
          </div>
          <h1 className="ml-2 text-xl font-semibold">
            {t('general.your')} {profileDataTypeToString(type)}
          </h1>
        </div>
        <div>
          {type !== ProfileDataType.BIRTHDATE && (
            <>
              <label htmlFor={`${modalField?.id}-label`} className="mb-1 pr-2 font-medium">
                {t('general.label')}:
              </label>

              <Select
                isSearchable={false}
                className="mb-4"
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? COLOR_PRIMARY_300 : `${COLOR_SECONDARY_200}80`,
                    borderRadius: '15px',
                    borderWidth: '2px',
                    color: COLOR_PRIMARY_900,
                    backgroundColor: `${COLOR_SECONDARY_200}80`,
                    boxShadow: 'none',
                    height: '50px',
                    fontWeight: '500',
                    fontSize: '16px',
                    '&:hover': {
                      borderColor: state.isFocused ? COLOR_PRIMARY_300 : `${COLOR_SECONDARY_200}80`,
                    },
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                    height: 'hug',
                  }),
                  option: (styles, { isSelected }) => {
                    return {
                      ...styles,
                      backgroundColor: isSelected ? COLOR_PRIMARY : COLOR_SECONDARY_50,
                      color: isSelected ? COLOR_SECONDARY_50 : COLOR_PRIMARY_900,
                      '&:hover': {
                        backgroundColor: isSelected ? COLOR_PRIMARY_300 : COLOR_PRIMARY_300,
                        color: COLOR_PRIMARY_900,
                      },
                    };
                  },
                  indicatorSeparator: () => ({
                    display: 'none',
                  }),
                  dropdownIndicator: (baseStyles, state) => ({
                    ...baseStyles,
                    color: state.isFocused ? COLOR_PRIMARY_300 : COLOR_PRIMARY,
                  }),
                }}
                options={labelOptions as any}
                value={{
                  label:
                    labelOptions.find((x) => x.value === modalField?.label)?.label! || 'General',
                  value:
                    labelOptions.find((x) => x.label === modalField?.label)?.value! || 'GENERAL',
                }}
                onChange={(v) => setModalField({ ...modalField!, label: v?.value! })}
              />
            </>
          )}

          <ProfileFieldForm field={modalField!} setField={setModalField} validate={validate} />
        </div>
        <div data-testid="buttons" className="mt-8 flex justify-end">
          <CancelSaveButton onCancel={() => setOpen(false)} onSave={handleSubmit} />
        </div>
      </div>
    </ModalLayout>
  );
}
